import type { Client, Folder } from '@efdigital/ll-addon-data';
import { DataTypeSystem } from '@efdigital/ll-addon-data/data-types';
import { findClient } from '@efdigital/ll-admin-v2/data/builders/system/clients';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

export type Breadcrumb = {
  id: string;
  name: string;
  isRoot: boolean;
  isActive: boolean;
  depth: number;
};

interface FolderBreadcrumbSignature {
  Element: HTMLDivElement;
  Args: {
    folderAndContents: Folder;
  };
  Blocks: {
    breadcrumb: [crumb: Breadcrumb];
  };
}

interface FolderBreadcrumbItemSignature {
  Args: {
    crumb: Breadcrumb;
    onClick: (crumb: Breadcrumb) => void;
  };
  Element: HTMLButtonElement;
}

export class FolderBreadcrumbItem extends Component<FolderBreadcrumbItemSignature> {
  @service declare store: Store;

  get isClientFolder() {
    return (
      this.args.crumb.depth === 1 &&
      !this.args.crumb.id.startsWith('|global|') &&
      this.args.crumb.id !== '|'
    );
  }

  clientTitle = async () => {
    const firstPart = this.args.crumb.id.split('|')[1]!;
    const client = this.store.peekRecord<Client>(
      DataTypeSystem.Client,
      firstPart
    );

    if (client?.title) {
      return client.title;
    }

    try {
      const response = await this.store.request(findClient(firstPart));
      return response.content.data.title;
    } catch (error) {
      console.error(error);
      return this.args.crumb.name;
    }
  };

  <template>
    <button
      class="btn btn-link p-0"
      type="button"
      {{on "click" (fn @onClick @crumb)}}
    >{{#if this.isClientFolder}}<Await @promise={{(this.clientTitle)}}><:success
            as |title|
          >{{title}}</:success></Await>{{else}}{{@crumb.name}}{{/if}}</button>
  </template>
}

export default class FolderBreadcrumb extends Component<FolderBreadcrumbSignature> {
  breadcrumb = (folderId: string | null): Breadcrumb[] => {
    if (!folderId) {
      return [] as Breadcrumb[];
    }

    const rootCrumb = {
      id: '|',
      name: 'Files',
      isRoot: true,
      isActive: false,
      depth: 0,
    };

    const crumbs =
      folderId.length <= 1
        ? [rootCrumb]
        : folderId
            .substring(1)
            .split('|')
            .reduce(
              ({ crumbs, currentPath }, name) => {
                const thisPath = currentPath + '|' + name;
                const crumb = {
                  id: thisPath,
                  name: name,
                  isRoot: false,
                  isActive: false,
                  depth: crumbs.length,
                };
                return {
                  crumbs: [...crumbs, crumb],
                  currentPath: thisPath,
                };
              },
              {
                crumbs: [rootCrumb],
                currentPath: '',
              }
            ).crumbs;

    crumbs[crumbs.length - 1]!.isActive = true;
    return crumbs;
  };

  <template>
    <nav aria-label="breadcrumb" ...attributes>
      <ol class="breadcrumb">
        {{#each (this.breadcrumb @folderAndContents.id) as |crumb|}}
          <li class="breadcrumb-item">{{yield crumb to="breadcrumb"}}</li>
        {{/each}}
      </ol>
    </nav>
  </template>
}
